import React, { useEffect, useState } from "react";  
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_BASE } from "../apiBase";
import { ownerSecretHeader } from "../ownerSecret";

console.log("EditEnvelopePage LOADED >>>");

/** ***************************************************************
 * useState: https://react.dev/reference/react/useState
 * - stores editable UI state like inputs
 * 
 * useEffect: https://react.dev/reference/react/useEffect
 * - loads the current envelope from server when a tail number changes
 * 
 * useNavigate: https://reactrouter.com/en/main/hooks/use-navigate
 * - allows redirecting to the main WtBalTable page after saving
 * 
 * useSearchParams: https://reactrouter.com/en/main/hooks/use-search-params
 * - reads the tail number from the URL query string
 * fetch: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
 * 
 * API_BASE: defined in src/apiBase.js, 
 * the base URL for API calls (toldcardclient.vercel.app/api)
 * 
 *  ***************************************************************
 */


/**
 * ---------------------------------------------------
 * helper function used to convert strings to numbers
 * this would be used during a save operation
 * envelope points are stored as numeric data
 * ---------------------------------------------------
 */
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * ---------------------------------------------------
 * function used to create the weight and balance envelope
 * -tail determines which aircraft's envelope is being edited
 *    on save, the envelope is sent to the server as part of the wtbals list
 * wtbals holds entire list of weight and balance rows
 * points holds just the envelope points being edited in the table
 * msg holds status messages for the user
 * 
 * it uses the Document Object Model (DOM) lifecycle for React components.
 * 
 * DOM elements are updated based on the new state.  
  * Note: the DOM tree is created in the browser using the HTML 
  * returned JSX from the component, 
  * and React manages the updates to the DOM efficiently when state changes occur.
  * 
  * Arrays and objects may be stored in the heap memory
  * when state is updated with new arrays or objects.
 * ---------------------------------------------------
 */
export default function EditEnvelopePage({currentAircraft}) {
  console.log("EditEnvelopePage FUNCTION ENTERED");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const tail = String(
    searchParams.get("tail") || 
    currentAircraft?.tailNumber||
    currentAircraft?.id ||
    currentAircraft?.tail ||
    ""
    ).trim().toUpperCase();


  console.log("EditEnvelopePage tail:", tail);
  const [wtbals, setWtbals] = useState([]);
  const [points, setPoints] = useState([]); // [{cgIn, weightLbs}]
  const [msg, setMsg] = useState("");


/**
 * ---------------------------------------------------
 * The anonymous asynchronous function inside useEffect 
 * - runs when the component mounts and whenever the tail variable changes
 * - it fetches the current weight and balance data for the specified tail number from the server
 * - it looks for a row with id "limits" in the fetched wtbals list, which is expected to contain the envelope data
 * 
 * useEffect mounts into the component Lifecycle
 * - when tail changes, it loads the current envelope from the server
 * - it looks for a wtbals row with id "limits" and uses its forwardEnvelope as the points to edit
 * - if no limits row or forwardEnvelope is found, it starts with an empty envelope
 * 
 * it also includes error handling to set a message if the load fails
 * 
 * ?: ternary operator. if the value is valid then use it, otherwise use the default (empty array or string)
 * ??: if the value is null or undefined, use the default (empty array or string)
 * ---------------------------------------------------
 */
  useEffect(() => {
    console.log("EditEnvelopePage useEffect triggered, tail:", tail);

    

    const load = async () => {
      try {
        setMsg("");

        if (!tail) {
          setMsg("No aircraft is selected.");
          return;
        }


        const url = `${API_BASE}/api/wtbals?tail=${encodeURIComponent(tail)}`;
        console.log("EditEnvelopePage fetch url:", url);

        const res = await fetch(url);
        console.log("EditEnvelopePage fetch status:", res.status);

        const json = await res.json();
        console.log("EditEnvelopePage fetched wtbals:", json);

        const list = Array.isArray(json) ? json : (json.wtbals || []);
        console.log("EditEnvelopePage processed wtbals list:", list);
        setWtbals(list);

        // using variable r, Stringify if the id is found then store it in limits.
        // otherwise use empty string, then lowercase for comparison or limits = null
        // strictly equal to limits
        const limits = list.find(r => String(r?.id ?? "").toLowerCase() === "limits") || null;
        console.log("EditEnvelopePage found limits:", limits);
        
        // test 'limits' (from Firebase database) to see if it is an array 
        // or an object with the CG forwardEnvelope property that is an array
        // 
        // in firebase, the object 'limits' is where the envelope points are stored
        // if 'limits' is found to store CG and weight points otherwise return 
        // empty string or empty array
        // note: the user interface starts pre-populated with current envelope points from firebase 
        const env = Array.isArray(limits?.forwardEnvelope) ? limits.forwardEnvelope : [];
        setPoints(env.map(p => ({
          cgIn: String(p.cgIn ?? ""),
          weightLbs: String(p.weightLbs ?? "")
        })));
      } catch (e) {
        setMsg("Load failed: " + String(e));
      }
    };
    load();  // call the async function to load data
  }, [tail]); // dependency array: re-run effect when 'tail' changes

  // adds a row to the envelope points table with empty CG and weight values
  const addPoint = () => {
    setPoints(prev => [...prev, { cgIn: "", weightLbs: "" }]);
  };

  // removes a row from the envelope points table based on its index 'idx'
  // note: filter is a method  used to create a new array
  // by interating over the existing points array
  // it includes all points where the index 'i' 
  // does not match the index 'idx' of the point to be removed 
  const removePoint = (idx) => {
    setPoints(prev => prev.filter((_, i) => i !== idx));
  };
  // the map method is used to create a new array of points where the point at index 'idx' 
  // is updated with the new value for the specified field (either 'cgIn' or 'weightLbs')
  const updatePoint = (idx, field, value) => {
    setPoints(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  /**
 * ---------------------------------------------------
 * The anonymous asynchronous save function 
 * - await can be used inside this function to 
 * handle asynchronous operations like fetching data from the server
 * 
 * 
 * .map:  every point 'p' a new object is created
 * ---with the same properties as 'p' but with the specified field updated to the new value
 * ---cgIn becomes a numeric value: string to number coversion
 * ---weightLbs becomes a numeric value: string to number conversion
 * 
 * .filter statement:  removes rows where cgIn or weightLbs are null
 * filter method works by creating new array that includes 
 * only the points where both cgIn and weightLbs are not null 
 * each point 'p' is checked to see if its cgIn and weightLbs properties are not null
 * 
 * ?: if the value is valid then use it, otherwise use the default (empty array or string)
 * 
 * ---------------------------------------------------
 */
  const save = async () => {
    try {
      setMsg("");  // clear previous messages


      if (!tail) {
          setMsg("Cannot save: no aircraft is selected.");
          return;
        }

      // normalize + validate
      const cleaned = points
        .map(p => ({ cgIn: toNum(p.cgIn), weightLbs: toNum(p.weightLbs) }))
        .filter(p => p.cgIn != null && p.weightLbs != null);

        // no valid points
      if (!cleaned.length) {
        setMsg("No valid points to save.");
        return;
      }

      /**
       * create shallow copy of 'wtbals' array to avoid mutating state directly
       * 'next' is a new array, don't mutate 'wtbals' directly
       * r? handle missing id. ?? use empty string if id missing.
       * finds array index whose id is 'limits'.  returns -1 if not found. 
       * copy object 'limits' row if li>=0 to next array
       * if not found, create new limits row with id 'limits'
       * set or replace forwardEnvelope field on the 'limits' object with cleaned array points
       *       if 'limits' row was found in 'wtbals', replace it with updated 'limits' object
       * replace that element in 'next' array with the updated 'limits' object
       * otherwise append 'limits' object to the end of 'next' array
       */ 
      const next = [...wtbals];  
      const li = next.findIndex(r => String(r?.id ?? "").toLowerCase() === "limits");
      const limits = li >= 0 ? { ...next[li] } : { id: "limits" };
      limits.forwardEnvelope = cleaned;
      if (li >= 0) next[li] = limits;
      else next.push(limits);

      // vercel serverless uses /api directory as entry point for API routes
      // PUT causes overwrite of entire wtbals list
      const url = `${API_BASE}/api/wtbals?tail=${encodeURIComponent(tail)}`;

      const authHeaders = ownerSecretHeader(tail);

        console.log("Envelope authorization:", {
          headerNames: Object.keys(authHeaders),
          usingAdminSecret: Boolean(authHeaders["X-Admin-Secret"]),
          usingOwnerSecret: Boolean(authHeaders["X-Owner-Secret"]),
        });

      const res = await fetch(url, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...authHeaders, 
        },
        body: JSON.stringify({ tail, wtbals: next })  // JSON payload sent to server includes envelope
      });

      const text = await res.text();
      if (!res.ok) {
        setMsg(`Save failed: ${res.status} ${text}`);
        return;
      }

      setMsg("Envelope saved.");
      navigate(`/wtbals?tail=${encodeURIComponent(tail)}`, { replace: true });
    } catch (e) {
      setMsg("Save error: " + String(e));
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto" }}>
      <h3>Edit Weight & Balance Envelope</h3>
      <div style={{ fontWeight: "bold", marginBottom: 8 }}>{msg}</div>

      <table id="edit-envelope">
        <thead>
          <tr>
            <th>CG (in)</th>
            <th>Weight (lbs)</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p, i) => (
            <tr key={i}>
              <td>
                <input
                  type="number"
                  value={p.cgIn}
                  onChange={(e) => updatePoint(i, "cgIn", e.target.value)}
                />
              </td>
              <td>
                <input
                  type="number"
                  value={p.weightLbs}
                  onChange={(e) => updatePoint(i, "weightLbs", e.target.value)}
                />
              </td>
              <td>
                <button type="button" onClick={() => removePoint(i)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="wtbal-controls">
        <button type="button" onClick={addPoint}>+ Add Point</button>
        <button type="button" onClick={save}>Save Envelope</button>
      </div>
    </div>
  );
}